// src/App.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ethers, TypedDataDomain, TypedDataField } from 'ethers';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { PREPARE_PATH, SUBMIT_PATH, TOKENS_PATH, getSwapsPath } from './config/api';
import { TOKEN_LIST } from './config/tokens';
import { Layout } from './components/layout';
import type {
  Token,
  PrepareRequest,
  PrepareResponse,
  EIP712TypedData,
  EIP712ExecuteSwapMessage,
  EIP712PermitMessage,
  SubmitMetaTxRequest,
  SubmitMetaTxResponse,
  SwapStatusResponse,
} from './types';
import {
  SWAP_STATUS_DISPLAY,
  TERMINAL_SWAP_STATUSES,
  EXPLORER_TX_URL,
  ERC20_ABI,
} from './types';

const App: React.FC = () => {
  // State
  const [isConnected, setIsConnected] = useState(false);
  const [userAddress, setUserAddress] = useState('');
  const [chainId, setChainId] = useState<number | null>(null);
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  
  // Form state
  const [inputToken, setInputToken] = useState<Token | null>(null);
  const [outputToken, setOutputToken] = useState<Token | null>(null);
  const [inputAmount, setInputAmount] = useState('');
  const [outputAmount, setOutputAmount] = useState('');
  const [slippageAuto, setSlippageAuto] = useState(true);
  const [slippageValue, setSlippageValue] = useState(1.3);
  const [slippageModalOpen, setSlippageModalOpen] = useState(false);
  const [slippageManualInput, setSlippageManualInput] = useState('');
  const [receiver, setreceiver] = useState('');
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [customTokenModalOpen, setCustomTokenModalOpen] = useState(false);
  const [customTokenAddress, setCustomTokenAddress] = useState('');
  const [customTokenSymbol, setCustomTokenSymbol] = useState('');
  const [customTokenDecimals, setCustomTokenDecimals] = useState('');
  const [inputBalance, setInputBalance] = useState<string | null>(null);
  const [outputBalance, setOutputBalance] = useState<string | null>(null);
  
  // Token list: allowed From addresses from API, custom tokens added by user
  const [allowedFromAddresses, setAllowedFromAddresses] = useState<Set<string>>(new Set());
  const [tokensLoading, setTokensLoading] = useState(true);
  const [customTokens, setCustomTokens] = useState<Token[]>([]);

  const allTokens = useMemo(
    () => [...TOKEN_LIST, ...customTokens],
    [customTokens]
  );
  const fromTokens = useMemo(
    () => allTokens.filter((t) => allowedFromAddresses.has(t.address.toLowerCase())),
    [allTokens, allowedFromAddresses]
  );
  const toTokens = allTokens;

  // Response state
  const [prepareResponse, setPrepareResponse] = useState<PrepareResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signStep, setSignStep] = useState<number>(0); // 0: not started, 1: permit, 2: execute
// State to hold signatures separately
  const [permitSignature, setPermitSignature] = useState<string | null>(null);
  const [executeSignature, setExecuteSignature] = useState<string | null>(null);

  // Swap tracking state (from submit-meta-tx response)
  const [swapTracking, setSwapTracking] = useState<{
    orderId: string;
    swapStatus: string;
    txHash?: string;
  } | null>(null);
  const swapPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Token dropdown (which block's selector is open)
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState<'from' | 'to' | null>(null);

  // Backend URLs
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  const PREPARE_ENDPOINT = `${API_BASE}${PREPARE_PATH}`;
  const SUBMIT_ENDPOINT = `${API_BASE}${SUBMIT_PATH}`;
  const TOKENS_ENDPOINT = `${API_BASE}${TOKENS_PATH}`;
  const getSwapsUrl = (orderId: string) => `${API_BASE}${getSwapsPath(orderId)}`;

  const rpcUrl = process.env.REACT_APP_RPC_URL || 'https://rpc.test.mezo.org/';
  const rpcProvider = useMemo(() => new ethers.JsonRpcProvider(rpcUrl), [rpcUrl]);

  // Fetch allowed token addresses for From block
  useEffect(() => {
    if (!API_BASE) {
      setTokensLoading(false);
      return;
    }
    let cancelled = false;
    fetch(TOKENS_ENDPOINT)
      .then((res) => res.json())
      .then((data: { status?: string; addresses?: string[] }) => {
        if (cancelled) return;
        const addrs = Array.isArray(data.addresses)
          ? data.addresses.map((a) => a.toLowerCase())
          : [];
        setAllowedFromAddresses(new Set(addrs));
      })
      .catch(() => {
        if (!cancelled) setAllowedFromAddresses(new Set());
      })
      .finally(() => {
        if (!cancelled) setTokensLoading(false);
      });
    return () => { cancelled = true; };
  }, [API_BASE, TOKENS_ENDPOINT]);

  // Sync manual slippage input when modal opens
  useEffect(() => {
    if (slippageModalOpen) setSlippageManualInput(slippageValue.toFixed(2));
  }, [slippageModalOpen]);

  // Clear input token if it is no longer in allowed From list
  useEffect(() => {
    if (tokensLoading || !inputToken) return;
    if (!allowedFromAddresses.has(inputToken.address.toLowerCase())) {
      setInputToken(null);
    }
  }, [tokensLoading, allowedFromAddresses, inputToken]);

  useEffect(() => {
    if (!userAddress) {
      setInputBalance(null);
      setOutputBalance(null);
      return;
    }
    let cancelled = false;
    const fetchBalances = async () => {
      if (!inputToken) {
        setInputBalance(null);
      } else {
        try {
          const contract = new ethers.Contract(inputToken.address, ERC20_ABI, rpcProvider);
          const raw = await contract.balanceOf(userAddress);
          if (!cancelled) {
            setInputBalance(ethers.formatUnits(raw, inputToken.decimals));
          }
        } catch {
          if (!cancelled) setInputBalance('—');
        }
      }
      if (!outputToken) {
        setOutputBalance(null);
      } else {
        try {
          const contract = new ethers.Contract(outputToken.address, ERC20_ABI, rpcProvider);
          const raw = await contract.balanceOf(userAddress);
          if (!cancelled) {
            setOutputBalance(ethers.formatUnits(raw, outputToken.decimals));
          }
        } catch {
          if (!cancelled) setOutputBalance('—');
        }
      }
    };
    fetchBalances();
    return () => { cancelled = true; };
  }, [userAddress, inputToken, outputToken, rpcProvider]);

  // Initialize on mount
  useEffect(() => {
    checkConnection();
    setupEventListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userAddress) {
      setreceiver(userAddress);
    }
  }, [userAddress]);

  useEffect(() => {
    return () => {
      if (swapPollIntervalRef.current) {
        clearInterval(swapPollIntervalRef.current);
      }
    };
  }, []);

  // Close token dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.token-selector') && !target.closest('.token-dropdown')) {
        setTokenDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSwapStatus = async (orderId: string): Promise<SwapStatusResponse | null> => {
    try {
      const response = await fetch(getSwapsUrl(orderId));
      if (!response.ok) return null;
      const data: SwapStatusResponse = await response.json();
      return data;
    } catch {
      return null;
    }
  };

  const startSwapTracking = (orderId: string) => {
    setSwapTracking({ orderId, swapStatus: 'TRANSACTION_STATUS_PENDING' });

    const poll = async () => {
      const data = await fetchSwapStatus(orderId);
      if (!data) return;

      setSwapTracking((prev) =>
        prev ? { ...prev, swapStatus: data.swap_status, txHash: data.tx_hash } : null
      );

      if (TERMINAL_SWAP_STATUSES.includes(data.swap_status)) {
        if (swapPollIntervalRef.current) {
          clearInterval(swapPollIntervalRef.current);
          swapPollIntervalRef.current = null;
        }
      }
    };

    swapPollIntervalRef.current = setInterval(poll, 3000);
    poll();
  };

  const checkConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWallet();
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const setupEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }
  };

  const handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      setUserAddress(accounts[0]);
    }
  };

  const handleChainChanged = (chainId: string) => {
    window.location.reload();
  };

  const connectWallet = async () => {
    try {
      if (typeof window.ethereum === 'undefined') {
        toast.error('Please install MetaMask!');
        return;
      }

      // Request account access
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });

      // Create provider and signer
      const newProvider = new ethers.BrowserProvider(window.ethereum);
      const newSigner = await newProvider.getSigner();
      const network = await newProvider.getNetwork();

      setProvider(newProvider);
      setSigner(newSigner);
      setUserAddress(accounts[0]);
      setChainId(Number(network.chainId));
      setIsConnected(true);

      toast.success('Wallet connected successfully!');
    } catch (error) {
      console.error('Error connecting wallet:', error);
      toast.error('Failed to connect wallet');
    }
  };

  const disconnectWallet = () => {
    setProvider(null);
    setSigner(null);
    setUserAddress('');
    setIsConnected(false);
    setPrepareResponse(null);
    toast.info('Wallet disconnected');
  };

  const handlePrepare = async () => {
    if (!inputToken || !outputToken || !signer) {
      toast.error('Please select tokens and connect wallet');
      return;
    }
    const hasMaxIn = inputAmount.trim() !== '' && parseFloat(inputAmount) > 0;
    const hasMinOut = outputAmount.trim() !== '' && parseFloat(outputAmount) > 0;
    if (!hasMaxIn && !hasMinOut) {
      toast.error('Please enter amount in (From) or amount out (To)');
      return;
    }
    const useMaxIn = hasMaxIn;

    setIsLoading(true);
    setPrepareResponse(null);
    setSignStep(0);
    setSwapTracking(null);

    try {
      const prepareRequest: PrepareRequest = {
        receiver: receiver || userAddress,
        token_in: inputToken.address,
        token_out: outputToken.address,
        user: userAddress,
      };
      if (!slippageAuto) {
        prepareRequest.slippage = Math.trunc(slippageValue * 100);
      }
      if (useMaxIn) {
        prepareRequest.token_in_max_amount = ethers.parseUnits(
          inputAmount,
          inputToken.decimals
        ).toString();
      } else if (hasMinOut) {
        prepareRequest.token_out_min_amount = ethers.parseUnits(
          outputAmount,
          outputToken.decimals
        ).toString();
      }

      const response = await fetch(PREPARE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: prepareRequest, protocol: 'swap' }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data: PrepareResponse = await response.json();

      setPrepareResponse(data);
  
      const executePayload = data.payload.eip712.find(
        (p): p is EIP712TypedData & { message: EIP712ExecuteSwapMessage } =>
          p.primaryType === "ExecuteSwap"
      );
  
      if (executePayload?.message?.swapData) {
        const { inputAmount: payloadInput, minAmountOut } = executePayload.message.swapData;
        if (minAmountOut) {
          setOutputAmount(ethers.formatUnits(minAmountOut, outputToken.decimals));
        }
        if (payloadInput) {
          const swapInput = BigInt(payloadInput);
          const feeInput = BigInt(data.payload.fee_data.input_amount);
          const totalInput = swapInput + feeInput;
          setInputAmount(ethers.formatUnits(totalInput, inputToken.decimals));
        }
      }
  
      toast.success('Prepare successful! Please sign the transactions.');
      setSignStep(1);
  
    } catch (error) {
      console.error('Error in prepare:', error);
      toast.error(`Prepare failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const signTypedData = async (typedData: EIP712TypedData): Promise<string> => {
    if (!signer || !provider) {
      throw new Error('Wallet not connected');
    }

    // Convert to EIP-712 format for ethers
    const domain = {
      name: typedData.domain.name,
      version: typedData.domain.version,
      chainId: parseInt(typedData.domain.chainId),
      verifyingContract: typedData.domain.verifyingContract,
    };

    const types = typedData.types as Record<string, TypedDataField[]>;
    
    // Sign the typed data
    const signature = await signer.signTypedData(
      domain as TypedDataDomain,
      types,
      typedData.message
    );

    return signature;
  };

  const signPermit = async () => {
    if (!prepareResponse) {
      toast.error('No permit data available');
      return;
    }
  
    const permitTypedData = prepareResponse.payload.eip712.find(
      (p): p is EIP712TypedData & { message: EIP712PermitMessage } =>
        p.primaryType === "Permit"
    );
  
    if (!permitTypedData) {
      toast.error('Permit typed data missing');
      return;
    }
  
    try {
      const signature = await signTypedData(permitTypedData);
      setPermitSignature(signature);
  
      toast.success('Permit signed successfully!');
      setSignStep(2); // Ready for execute signature
  
    } catch (error) {
      console.error('Error signing permit:', error);
      toast.error(`Failed to sign permit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const signExecute = async () => {
    if (!prepareResponse) {
      toast.error('No execute data available');
      return;
    }
  
    const executeTypedData = prepareResponse.payload.eip712.find(
      (p): p is EIP712TypedData & { message: EIP712ExecuteSwapMessage } =>
        p.primaryType === "ExecuteSwap"
    );
  
    if (!executeTypedData) {
      toast.error('Execute typed data missing');
      return;
    }
  
    try {
      const signature = await signTypedData(executeTypedData);
      setExecuteSignature(signature);
  
      toast.success('Execute signed successfully! Ready to submit.');
      setSignStep(3); // Ready for submission
  
    } catch (error) {
      console.error('Error signing execute:', error);
      toast.error(`Failed to sign execute: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  
  const submitTransaction = async () => {
    if (
      !prepareResponse ||
      !permitSignature ||
      !executeSignature
    ) {
      toast.error('Both signatures required');
      return;
    }
  
    setIsLoading(true);
  
    try {
      const submitPayload: SubmitMetaTxRequest = {
        fee_data: prepareResponse.payload.fee_data,
        eip712: prepareResponse.payload.eip712,
        service_signature: prepareResponse.payload.service_signature,
        fee_amount: prepareResponse.payload.fee_amount,
        permit_signature: permitSignature,
        execute_swap_signature: executeSignature
      };
  
      const response = await fetch(SUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: submitPayload, protocol: 'swap' })
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result: SubmitMetaTxResponse = await response.json();
  
      toast.success('Transaction submitted successfully!');
      console.log('Submit response:', result);

      const orderId = result.payload?.order_id ?? result.order_id;
      if (result.status === 'ok' && orderId) {
        startSwapTracking(orderId);
      }
  
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error(
        `Submit failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleInputAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setInputAmount(value);
      setOutputAmount('');
      if (prepareResponse) {
        setPrepareResponse(null);
        setSignStep(0);
      }
    }
  };

  const handleOutputAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setOutputAmount(value);
      setInputAmount('');
      if (prepareResponse) {
        setPrepareResponse(null);
        setSignStep(0);
      }
    }
  };

  const slippageDisplay = (() => {
    if (slippageAuto) {
      if (prepareResponse != null) {
        const pct = prepareResponse.payload.slippage / 100;
        return `Auto: ${pct.toFixed(2)}%`;
      }
      return 'Auto: —';
    }
    return `${Number(slippageValue.toFixed(2))}%`;
  })();

  const handleSlippageManualInput = (raw: string) => {
    let normalized = '';
    let seenDot = false;
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (c >= '0' && c <= '9') normalized += c;
      else if ((c === '.' || c === ',') && !seenDot) {
        normalized += '.';
        seenDot = true;
      }
    }
    setSlippageManualInput(normalized);
    setSlippageAuto(false);
    if (normalized === '' || normalized === '.') return;
    const parsed = parseFloat(normalized);
    if (isNaN(parsed) || parsed < 0.1 || parsed > 100) return;
    const parts = normalized.split('.');
    if (parts.length === 2 && parts[1].length > 2) return;
    setSlippageValue(parsed);
  };

  const handleSlippageManualBlur = () => {
    const normalized = slippageManualInput.replace(',', '.');
    if (normalized === '' || normalized === '.') {
      setSlippageManualInput('1.30');
      setSlippageValue(1.3);
      return;
    }
    const parsed = parseFloat(normalized);
    if (isNaN(parsed) || parsed < 0.1) {
      setSlippageManualInput(slippageValue.toFixed(2));
      return;
    }
    const clamped = Math.min(100, Math.max(0.1, parsed));
    setSlippageManualInput(clamped.toFixed(2));
    setSlippageValue(clamped);
  };

  const slippageInputValue = slippageAuto
    ? 'Auto'
    : slippageManualInput;

  const submitCustomToken = () => {
    const address = customTokenAddress.trim();
    const symbol = customTokenSymbol.trim();
    const decimalsStr = customTokenDecimals.trim();
    if (!address || !symbol || !decimalsStr) {
      toast.error('Please fill all fields');
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      toast.error('Invalid token address');
      return;
    }
    const decimals = parseInt(decimalsStr, 10);
    if (isNaN(decimals) || decimals < 0 || decimals > 18) {
      toast.error('Decimals must be 0–18');
      return;
    }
    if (customTokens.some((t) => t.address.toLowerCase() === address.toLowerCase())) {
      toast.error('Token with this address already added');
      return;
    }
    const newToken: Token = { address, symbol, name: symbol, decimals };
    setCustomTokens((prev) => [...prev, newToken]);
    toast.success('Custom token added!');
    setCustomTokenModalOpen(false);
    setCustomTokenAddress('');
    setCustomTokenSymbol('');
    setCustomTokenDecimals('');
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Slippage Setting modal */}
      {slippageModalOpen && (
        <div className="modal-overlay" onClick={() => setSlippageModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="slippage-modal-title">
          <div className="modal-panel slippage-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="slippage-modal-title" className="modal-title">Slippage Setting</h2>
              <button type="button" className="modal-close" onClick={() => setSlippageModalOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="slippage-presets">
              <button type="button" className={`slippage-preset-btn ${slippageAuto ? 'selected' : ''}`} onClick={() => setSlippageAuto(true)}>Auto</button>
              <button type="button" className={`slippage-preset-btn ${!slippageAuto && slippageValue === 1.3 ? 'selected' : ''}`} onClick={() => { setSlippageValue(1.3); setSlippageAuto(false); setSlippageManualInput('1.3'); }}>1.3%</button>
              <button type="button" className={`slippage-preset-btn ${!slippageAuto && slippageValue === 1.5 ? 'selected' : ''}`} onClick={() => { setSlippageValue(1.5); setSlippageAuto(false); setSlippageManualInput('1.5'); }}>1.5%</button>
              <button type="button" className={`slippage-preset-btn ${!slippageAuto && slippageValue === 2 ? 'selected' : ''}`} onClick={() => { setSlippageValue(2); setSlippageAuto(false); setSlippageManualInput('2'); }}>2%</button>
              <div className="slippage-manual-wrap">
                <input
                  type="text"
                  className="slippage-manual-input"
                  value={slippageInputValue}
                  onChange={(e) => handleSlippageManualInput(e.target.value)}
                  onBlur={handleSlippageManualBlur}
                  aria-label="Slippage percentage"
                />
                {slippageInputValue !== 'Auto' && <span className="slippage-manual-suffix">%</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Token modal */}
      {customTokenModalOpen && (
        <div className="modal-overlay" onClick={() => setCustomTokenModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="custom-token-modal-title">
          <div className="modal-panel custom-token-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="custom-token-modal-title" className="modal-title">Add Custom Token</h2>
              <button type="button" className="modal-close" onClick={() => setCustomTokenModalOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="modal-body-blocks">
                <div className="settings-section">
                  <label>Token contract address</label>
                  <input
                    type="text"
                    className="address-input"
                    placeholder="0x..."
                    value={customTokenAddress}
                    onChange={(e) => setCustomTokenAddress(e.target.value)}
                  />
                </div>
                <div className="settings-section">
                  <label>Symbol</label>
                  <input
                    type="text"
                    className="address-input"
                    placeholder="e.g. USDT"
                    value={customTokenSymbol}
                    onChange={(e) => setCustomTokenSymbol(e.target.value)}
                  />
                </div>
                <div className="settings-section">
                  <label>Decimals (0–18)</label>
                  <input
                    type="text"
                    className="address-input"
                    placeholder="18"
                    value={customTokenDecimals}
                    onChange={(e) => setCustomTokenDecimals(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="modal-btn modal-btn-secondary" onClick={() => setCustomTokenModalOpen(false)}>Cancel</button>
                <button type="button" className="modal-btn modal-btn-primary" onClick={submitCustomToken}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Layout
        isConnected={isConnected}
        userAddress={userAddress}
        chainId={chainId}
        onConnect={connectWallet}
        onDisconnect={disconnectWallet}
      >
        <div className="swap-card">
          <h2>Gasless Swap</h2>
          <p className="swap-subtitle">
            Swap tokens on Mezo without native tokens
            <span
              className="tooltip-trigger"
              data-tooltip={isConnected ? 'Select tokens, enter amount, and click Prepare Swap to get started.' : 'Connect your wallet to begin.'}
              aria-label={isConnected ? 'Select tokens, enter amount, and click Prepare Swap to get started.' : 'Connect your wallet to begin.'}
            >
              ?
            </span>
          </p>

          <div className="swap-card-content">
              {/* From Token */}
              <div className="input-section">
                <div className="input-section-header">
                  <label>From</label>
                  <div className="token-info token-info-above">
                    <span className="token-info-label">💵</span>
                    {userAddress && inputToken && inputBalance != null && inputBalance !== '…' && inputBalance !== '—' && parseFloat(inputBalance) > 0 ? (
                      <button
                        type="button"
                        className="balance-max-btn"
                        onClick={() => {
                          setInputAmount(inputBalance);
                          setOutputAmount('');
                          if (prepareResponse) {
                            setPrepareResponse(null);
                            setSignStep(0);
                          }
                        }}
                        disabled={isLoading}
                      >
                        {inputBalance}
                      </button>
                    ) : (
                      <span>{!userAddress || !inputToken ? '—' : (inputBalance ?? '…')}</span>
                    )}
                  </div>
                </div>
                <div className="token-block">
                  <div className="token-block-left">
                    <div
                      className={`token-selector ${tokenDropdownOpen === 'from' ? 'open' : ''}`}
                      onClick={() => setTokenDropdownOpen(v => v === 'from' ? null : 'from')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setTokenDropdownOpen(v => v === 'from' ? null : 'from')}
                    >
                      <div className="token-symbol-row">
                        {inputToken ? inputToken.symbol : 'Select token'}
                        <span className="token-selector-chevron">▾</span>
                      </div>
                      <div className="token-chain">Mezo chain</div>
                    </div>
                    {tokenDropdownOpen === 'from' && (
                      <div className="token-dropdown">
                        {tokensLoading ? (
                          <div className="token-dropdown-item">Loading…</div>
                        ) : (
                          fromTokens.map(token => (
                            <button
                              key={token.address}
                              type="button"
                              className={`token-dropdown-item ${inputToken?.address === token.address ? 'selected' : ''}`}
                              onClick={() => {
                                setInputToken(token);
                                setTokenDropdownOpen(null);
                              }}
                              disabled={isLoading}
                            >
                              {token.symbol}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div className="token-block-right">
                    <input
                      type="text"
                      className="amount-input"
                      placeholder="0.0"
                      value={inputAmount}
                      onChange={handleInputAmountChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="swap-arrow"
                onClick={() => {
                  setInputToken(outputToken);
                  setOutputToken(inputToken);
                  setInputAmount(outputAmount);
                  setOutputAmount(inputAmount);
                }}
                disabled={isLoading}
                aria-label="Swap input and output tokens"
              >
                ⇅
              </button>

              {/* To Token */}
              <div className="input-section">
                <div className="input-section-header">
                  <label>To</label>
                  <div className="token-info token-info-above">
                    <span className="token-info-label">💵</span>
                    <span>{!userAddress || !outputToken ? '—' : (outputBalance ?? '…')}</span>
                  </div>
                </div>
                <div className="token-block">
                  <div className="token-block-left">
                    <div
                      className={`token-selector ${tokenDropdownOpen === 'to' ? 'open' : ''}`}
                      onClick={() => setTokenDropdownOpen(v => v === 'to' ? null : 'to')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setTokenDropdownOpen(v => v === 'to' ? null : 'to')}
                    >
                      <div className="token-symbol-row">
                        {outputToken ? outputToken.symbol : 'Select token'}
                        <span className="token-selector-chevron">▾</span>
                      </div>
                      <div className="token-chain">Mezo chain</div>
                    </div>
                    {tokenDropdownOpen === 'to' && (
                      <div className="token-dropdown">
                        {toTokens.map(token => (
                          <button
                            key={token.address}
                            type="button"
                            className={`token-dropdown-item ${outputToken?.address === token.address ? 'selected' : ''}`}
                            onClick={() => {
                              setOutputToken(token);
                              setTokenDropdownOpen(null);
                            }}
                            disabled={isLoading}
                          >
                            {token.symbol}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="token-block-right">
                    <input
                      type="text"
                      className="amount-input"
                      placeholder="0.0"
                      value={outputAmount}
                      onChange={handleOutputAmountChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Slippage Tolerance row - pill opens modal */}
              <div className="slippage-row">
                <label className="slippage-label">Slippage Tolerance</label>
                <button
                  type="button"
                  className="slippage-pill"
                  onClick={() => setSlippageModalOpen(true)}
                  disabled={isLoading}
                  aria-label="Slippage setting"
                >
                  <span className="slippage-pill-value">{slippageDisplay}</span>
                  <span className="slippage-pill-edit" aria-hidden>✎</span>
                </button>
              </div>

              {/* Settings (Receiver only) - hidden by default */}
              <div className="settings-collapsible">
                <button
                  type="button"
                  className="settings-collapsible-trigger"
                  onClick={() => setSettingsExpanded((v) => !v)}
                  aria-expanded={settingsExpanded}
                  aria-controls="settings-collapsible-content"
                >
                  <span>Settings</span>
                  <span className="settings-collapsible-chevron" aria-hidden>{settingsExpanded ? '▾' : '▸'}</span>
                </button>
                <div id="settings-collapsible-content" className="settings-collapsible-content" hidden={!settingsExpanded}>
                  <div className="settings-section">
                    <label>Receiver Address (optional)</label>
                    <input
                      type="text"
                      className="address-input"
                      placeholder={userAddress || '0x...'}
                      value={receiver}
                      onChange={(e) => setreceiver(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Main Action Button */}
              {!isConnected ? (
                <button className="action-btn" onClick={connectWallet}>
                  Connect Wallet
                </button>
              ) : (
                <button
                  className={`action-btn ${isLoading ? 'loading' : ''}`}
                  onClick={handlePrepare}
                  disabled={
                    !inputToken ||
                    !outputToken ||
                    isLoading ||
                    (!(inputAmount.trim() !== '' && parseFloat(inputAmount) > 0) &&
                      !(outputAmount.trim() !== '' && parseFloat(outputAmount) > 0))
                  }
                >
                  {isLoading ? 'Processing...' : 'Prepare Swap'}
                </button>
              )}

              <button
                className="custom-token-btn"
                onClick={() => setCustomTokenModalOpen(true)}
                type="button"
              >
                + Add Custom Token
              </button>

            {prepareResponse && (
                <div className="signing-steps">
                  <h3>Signing Steps</h3>
                  <div className={`step ${signStep >= 1 ? 'active' : ''}`}>
                    <div className="step-header">
                      <span className="step-number">1</span>
                      <span className="step-title">Sign Permit</span>
                      {signStep > 1 && <span className="step-check">✓</span>}
                    </div>
                    {signStep === 1 && (
                      <button className="sign-btn" onClick={signPermit} disabled={isLoading}>
                        Sign Permit
                      </button>
                    )}
                  </div>
                  <div className={`step ${signStep >= 2 ? 'active' : ''}`}>
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <span className="step-title">Sign Execute</span>
                      {signStep > 2 && <span className="step-check">✓</span>}
                    </div>
                    {signStep === 2 && (
                      <button className="sign-btn" onClick={signExecute} disabled={isLoading}>
                        Sign Execute Swap
                      </button>
                    )}
                  </div>
                  {signStep === 3 && (
                    <div className="step active">
                      <div className="step-header">
                        <span className="step-number">3</span>
                        <span className="step-title">Submit Transaction</span>
                      </div>
                      <button className="submit-btn" onClick={submitTransaction} disabled={isLoading}>
                        {isLoading ? 'Submitting...' : 'Submit Transaction'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {swapTracking && (
                <div className="swap-status-section">
                  <h3>Transaction Status</h3>
                  <div className="swap-status-content">
                    {(() => {
                      const display = SWAP_STATUS_DISPLAY[swapTracking.swapStatus] ?? {
                        emoji: '❓',
                        label: swapTracking.swapStatus,
                      };
                      return (
                        <>
                          <div className="swap-status-row">
                            <span className="swap-status-badge">
                              {display.emoji} {display.label}
                            </span>
                          </div>
                          <div className="detail-row">
                            <span>Order ID:</span>
                            <span className="order-id">{swapTracking.orderId}</span>
                          </div>
                          {swapTracking.txHash && (
                            <div className="detail-row">
                              <span>Transaction:</span>
                              <a
                                href={`${EXPLORER_TX_URL}${swapTracking.txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tx-link"
                              >
                                {swapTracking.txHash.slice(0, 10)}...{swapTracking.txHash.slice(-8)}
                              </a>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {prepareResponse && (
                <div className="response-section">
                  <h3>Transaction Details</h3>
                  <div className="response-details">
                    <div className="detail-row">
                      <span>Input Amount:</span>
                      <span>{inputAmount} {inputToken?.symbol}</span>
                    </div>
                    <div className="detail-row">
                      <span>Min Output:</span>
                      <span>{outputAmount} {outputToken?.symbol}</span>
                    </div>
                    <div className="detail-row">
                      <span>Slippage:</span>
                      <span>{slippageAuto && prepareResponse ? `Auto: ${(prepareResponse.payload.slippage / 100).toFixed(2)}%` : `${Number(slippageValue.toFixed(2))}%`}</span>
                    </div>
                    {prepareResponse.payload.eip712.some(p => p.primaryType === "ExecuteSwap") && (() => {
                      const executePayload = prepareResponse.payload.eip712.find(
                        (p): p is EIP712TypedData & { message: EIP712ExecuteSwapMessage } =>
                          p.primaryType === "ExecuteSwap"
                      );
                      if (!executePayload) return null;
                      return (
                        <>
                          <div className="detail-row">
                            <span>Execution Contract:</span>
                            <span className="address">
                              {executePayload.message.swapData.executionContract.slice(0, 10)}...
                            </span>
                          </div>
                          <div className="detail-row">
                            <span>Relayer Fee:</span>
                            <span>{prepareResponse.payload.relayer_fee_percent/100}%</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
          </div>
        </div>
      </Layout>
    </>
  );
};

// Declare ethereum on window
declare global {
  interface Window {
    ethereum: any;
  }
}

export default App;